import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { FiSave, FiDownload, FiArrowLeft, FiZoomIn, FiZoomOut, FiMaximize } from 'react-icons/fi';
import { LineArtSettings, CleanupSettings } from '../components/editor/SettingsPanel';
import { imagesApi, processingApi } from '../services/api';
import type { ColoringPage } from '../types';

type ProcessingType = 'lineart' | 'cleanup' | 'watermark';

export function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [image, setImage] = useState<ColoringPage | null>(null);
  const [processingType, setProcessingType] = useState<ProcessingType>(
    (searchParams.get('type') as ProcessingType) || 'lineart'
  );
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingSvg, setDownloadingSvg] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);

  const [lineArtSettings, setLineArtSettings] = useState({
    algorithm: 'ai' as 'ai' | 'canny',
    edgeStrength: 0.5,
    cannyThresholdLow: 50,
    cannyThresholdHigh: 150,
    blurKernelSize: 5,
    lineThickness: 1,
    invertColors: true,
  });

  const [cleanupSettings, setCleanupSettings] = useState({
    blurRadius: 0.5,
    contrast: 1.5,
    brightness: 1.0,
    threshold: 0.5,
    sharpenAmount: 0.5,
  });

  useEffect(() => {
    if (id) {
      loadImage();
    }
  }, [id]);

  const loadImage = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await imagesApi.getById(parseInt(id));
      setImage(data);
    } catch (error) {
      console.error('Failed to load image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!image) return;
    if (processingType === 'watermark') return; // Watermark uses separate mask-based flow
    setProcessing(true);

    try {
      const blob = await processingApi.preview({
        imageId: image.id,
        processingType: processingType as 'lineart' | 'cleanup',
        lineArtOptions: processingType === 'lineart' ? lineArtSettings : undefined,
        cleanupOptions: processingType === 'cleanup' ? cleanupSettings : undefined,
      });

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.error('Failed to generate preview:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!image) return;
    setSaving(true);

    try {
      let result;
      if (processingType === 'lineart') {
        result = await processingApi.convertToLineArt({
          imageId: image.id,
          ...lineArtSettings,
        });
      } else if (processingType === 'cleanup') {
        result = await processingApi.cleanup({
          imageId: image.id,
          ...cleanupSettings,
        });
      }

      if (result) {
        navigate(`/editor/${result.id}?saved=true`);
      }
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (previewUrl) {
      const a = document.createElement('a');
      a.href = previewUrl;
      a.download = `${image?.title || 'coloring-page'}_${processingType}.png`;
      a.click();
    } else if (image) {
      window.open(imagesApi.getFileUrl(image.id), '_blank');
    }
  };

  const handleDownloadSvg = async () => {
    if (!image) return;
    setDownloadingSvg(true);
    try {
      await processingApi.downloadSvg(
        image.id,
        image.title || image.originalFileName
      );
    } catch (error) {
      console.error('Failed to download SVG:', error);
    } finally {
      setDownloadingSvg(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!image) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-gray-600 mb-4">Image not found</p>
        <button onClick={() => navigate('/gallery')} className="text-indigo-600 hover:text-indigo-700">
          Back to Gallery
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-semibold text-gray-900">{image.title || image.originalFileName}</h1>
            <p className="text-sm text-gray-500">
              {image.width} × {image.height}px
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setZoom(Math.max(25, zoom - 25))}
              className="p-2 text-gray-600 hover:bg-white rounded"
            >
              <FiZoomOut className="w-4 h-4" />
            </button>
            <span className="px-2 text-sm text-gray-600 min-w-[4rem] text-center">{zoom}%</span>
            <button
              onClick={() => setZoom(Math.min(200, zoom + 25))}
              className="p-2 text-gray-600 hover:bg-white rounded"
            >
              <FiZoomIn className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={handleDownload}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium flex items-center gap-2"
          >
            <FiDownload className="w-4 h-4" />
            PNG
          </button>

          <button
            onClick={handleDownloadSvg}
            disabled={downloadingSvg}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium flex items-center gap-2
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiMaximize className="w-4 h-4" />
            {downloadingSvg ? 'Converting...' : 'SVG'}
          </button>

          <button
            onClick={handleSave}
            disabled={saving || !previewUrl}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-medium
                       hover:shadow-lg hover:shadow-indigo-500/25 transition-all flex items-center gap-2
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 bg-gray-100 p-8 overflow-auto flex items-center justify-center">
          <div className="flex gap-8">
            {/* Original Image */}
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Original</p>
              <div
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
              >
                <img
                  src={imagesApi.getFileUrl(image.id)}
                  alt="Original"
                  className="max-w-md"
                />
              </div>
            </div>

            {/* Processed Preview */}
            {previewUrl && (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Preview</p>
                <div
                  className="bg-white rounded-xl shadow-lg overflow-hidden"
                  style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
                >
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-md"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Settings Sidebar */}
        <aside className="w-80 bg-white border-l border-gray-200 p-6 overflow-y-auto">
          {/* Processing Type Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Processing Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setProcessingType('lineart'); setPreviewUrl(null); }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  processingType === 'lineart'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Line Art
              </button>
              <button
                onClick={() => { setProcessingType('cleanup'); setPreviewUrl(null); }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  processingType === 'cleanup'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Cleanup
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {processingType === 'lineart' && (
            <LineArtSettings
              settings={lineArtSettings}
              onChange={setLineArtSettings}
              onPreview={handlePreview}
              isLoading={processing}
            />
          )}

          {processingType === 'cleanup' && (
            <CleanupSettings
              settings={cleanupSettings}
              onChange={setCleanupSettings}
              onPreview={handlePreview}
              isLoading={processing}
            />
          )}
        </aside>
      </div>
    </div>
  );
}
